import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { browserAnalyticsOptedOut, hasPendingAnalyticsObjection, objectToAnalytics } from "../../lib/analytics";
import { ONBOARDING_ACKNOWLEDGEMENTS } from "../../data/onboardingAcknowledgements";

/** Source-owned disclosures and preferences stay separate from published policy text. */
export function PolicySupplements({ kind }: { kind: string }) {
  const [analyticsStatus, setAnalyticsStatus] = useState<{
    enabled: boolean; optedOut: boolean; pending: boolean; serverSaved: boolean;
  } | null>(null);
  const [analyticsBusy, setAnalyticsBusy] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  useEffect(() => {
    if (kind !== "privacy") return;
    void hasPendingAnalyticsObjection().then((pending) => {
      if (!pending) return;
      setAnalyticsStatus((current) => ({
        enabled: current?.enabled ?? false,
        optedOut: true,
        pending: true,
        serverSaved: current?.serverSaved ?? false,
      }));
    });
    api.analyticsConfig().then(async (config) => {
      const pending = await hasPendingAnalyticsObjection();
      setAnalyticsStatus({
        enabled: config.enabled,
        optedOut: config.memberOptedOut || browserAnalyticsOptedOut() || pending,
        pending,
        serverSaved: config.memberOptedOut,
      });
    })
      .catch(() => setAnalyticsError("Could not load analytics preference."));
  }, [kind]);

  async function stopAnalytics() {
    setAnalyticsBusy(true); setAnalyticsError(null);
    try {
      const serverSaved = await objectToAnalytics();
      setAnalyticsStatus((current) => ({
        enabled: current?.enabled ?? false,
        optedOut: true,
        pending: false,
        serverSaved: serverSaved || (current?.serverSaved ?? false),
      }));
    } catch (error) {
      setAnalyticsStatus((current) => ({
        enabled: current?.enabled ?? false,
        optedOut: true,
        pending: true,
        serverSaved: current?.serverSaved ?? false,
      }));
      setAnalyticsError(error instanceof Error ? error.message : "Could not save analytics objection.");
    } finally { setAnalyticsBusy(false); }
  }

  return <>
      {kind === "terms" && <section className="card p-6 mt-4" aria-labelledby="account-acknowledgements-heading">
        <h2 id="account-acknowledgements-heading" className="text-lg font-bold text-navy">Account acknowledgements</h2>
        <ul className="mt-3 list-disc space-y-3 pl-5 text-sm leading-relaxed text-navy/80">
          {ONBOARDING_ACKNOWLEDGEMENTS.map(({ key, text }) => <li key={key}>{text}</li>)}
        </ul>
      </section>}
      {kind === "privacy" && <section className="card p-6 mt-4" aria-label="Analytics privacy">
        <h2 className="font-bold text-navy">Analytics privacy</h2>
        <p className="text-sm text-mut mt-1">First-party analytics measure visits and marketplace feature use. Google Analytics 4 measures public web page views. Objecting stops both in this browser and, if signed in, records a first-party objection on your account without affecting marketplace access.</p>
        {analyticsStatus?.pending ? <p className="text-sm text-mut mt-3">Analytics are turned off locally. Server cleanup is still pending; retry to finish saving your objection.</p> : null}
        {analyticsStatus?.serverSaved && !analyticsStatus.pending ? <p className="text-sm text-brand-green mt-3">Your analytics objection is saved on the server.</p> : null}
        {analyticsStatus?.optedOut && !analyticsStatus.serverSaved && !analyticsStatus.pending ? <p className="text-sm text-mut mt-3">Analytics are turned off in this browser.</p> : null}
        {(!analyticsStatus?.serverSaved || analyticsStatus.pending) && (
          <button type="button" onClick={stopAnalytics} disabled={analyticsBusy} className="btn-outline mt-3 disabled:opacity-60">
            {analyticsBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {analyticsStatus?.pending ? "Retry saving objection" : analyticsStatus?.optedOut ? "Complete analytics objection" : "Object to analytics"}
          </button>
        )}
        {analyticsError && <p className="text-sm text-brand-red mt-2">{analyticsError}</p>}
      </section>}
  </>;
}
